/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import i18n from "@/plugins/I18nPlugin";
import { CurrencyEnum } from "@/lib/enum/currency.enum";

/**
 * Localized grouping of a plain number. Shared building block for the currency
 * formatters below so grouping/rounding behaviour stays consistent.
 * @param value Amount to format.
 * @param locale BCP-47 locale for grouping.
 * @param maximumFractionDigits Optional cap on fraction digits.
 */
function formatLocalizedNumber(value: number, locale: string, maximumFractionDigits?: number): string {
  return value.toLocaleString(locale, maximumFractionDigits !== undefined ? { maximumFractionDigits } : undefined);
}

/**
 * Full currency formatter using the platform's real currency symbols (Intl
 * `style: "currency"`), e.g. `1.234,56 €`. Use this when the exact currency and
 * cents matter.
 * @param value Amount to format.
 * @param currency ISO 4217 currency to render.
 * @param maximumFractionDigits Optional cap on fraction digits.
 */
export function formatCurrency(value: number, currency: CurrencyEnum, maximumFractionDigits?: number) {
  if (!value) value = 0;

  return Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency,
    ...(maximumFractionDigits !== undefined ? { maximumFractionDigits } : {})
  }).format(value);
}

/**
 * Compact EUR formatter shared across the Fuhrparkkosten dashboard (hero,
 * ranking cards, container): abbreviates to `k€` / `Mio €` for large amounts so
 * chart labels and KPI values stay legible. Previously duplicated verbatim in
 * three components.
 *
 * Unlike {@link formatCurrency} this keeps a plain `" €"` suffix (no
 * non-breaking space, symbol always trailing) so the compact labels render
 * predictably regardless of locale.
 * @param value Amount in EUR.
 * @param locale BCP-47 locale for grouping; defaults to the active i18n locale.
 * @returns Compact, localized EUR string.
 */
export function formatEur(value: number, locale: string = i18n.locale): string {
  const safe = Number.isFinite(value) ? value : 0;

  if (safe >= 1_000_000) {
    return formatLocalizedNumber(safe / 1_000_000, locale, 1) + " Mio €";
  }
  if (safe >= 1_000) {
    return formatLocalizedNumber(Math.round(safe / 1_000), locale) + " k€";
  }

  return formatLocalizedNumber(Math.round(safe), locale) + " €";
}

/**
 * All ISO 4217 currencies with EUR and CHF pinned to the front, e.g. for
 * currency select dropdowns. The rest keeps its natural (alphabetical) order.
 */
export function getRelevantCurrencies(): CurrencyEnum[] {
  return [
    CurrencyEnum.EUR,
    CurrencyEnum.CHF,
    ...Object.values(CurrencyEnum).filter((currency) => currency !== CurrencyEnum.EUR && currency !== CurrencyEnum.CHF)
  ];
}

const currencyFractionDigitsCache = new Map<string, number>();

/**
 * Number of fraction digits (minor units) of an ISO 4217 currency, e.g. 2 for EUR, 0 for JPY,
 * 3 for BHD. Falls back to 2 for codes the runtime does not know.
 *
 * @param currency the ISO 4217 currency code
 */
export function getCurrencyFractionDigits(currency: CurrencyEnum): number {
  let digits = currencyFractionDigitsCache.get(currency);

  if (digits === undefined) {
    try {
      // the locale only affects formatting; the minor unit digit count is a property of the currency
      digits = Intl.NumberFormat("de-DE", { style: "currency", currency }).resolvedOptions().maximumFractionDigits;
    } catch {
      digits = 2;
    }
    currencyFractionDigitsCache.set(currency, digits);
  }

  return digits;
}
